import { useTranslation } from "react-i18next";
import {
  Activity,
  BadgeCheck,
  Bell,
  BellRing,
  BookOpen,
  ChartColumn,
  ChartLine,
  CircleCheck,
  ClipboardCheck,
  Cloud,
  FileBadge,
  FileText,
  Files,
  Gauge,
  Globe,
  Landmark,
  GraduationCap,
  KeyRound,
  Layers,
  Lightbulb,
  Link2,
  Lock,
  LogIn,
  Network,
  Palette,
  RadioTower,
  School,
  Search,
  Server,
  Settings,
  ShieldCheck,
  Users,
  Wifi,
  Zap,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { MenuGroup } from "../../components/Header/HeaderMenu";

// Textos em produtos_page.* nos locales (pt é o idioma de fallback). Aqui só a estrutura: ids e ícones.

export type Product = {
  id: string;
  Icon: LucideIcon;
  name: string;
  accent: string;
  heading: string;
  points: string[];
  pillars: { Icon: LucideIcon; title: string }[];
  tip: string;
};

export type Core = {
  id: string;
  Icon: LucideIcon;
  name: string;
  accent: string;
  text: string;
  flowLabel: string;
  flow: string[];
  features: { Icon: LucideIcon; title: string; text: string }[];
  tip: string;
};

// ícones dos pilares na mesma ordem dos textos; acronym = o nome é uma sigla e o accent é o nome por extenso
// (no menu do header aparece "DAP · Data Access Platform")
const PRODUCTS_CONFIG: { id: string; key: string; Icon: LucideIcon; pillars: LucideIcon[]; acronym?: boolean }[] = [
  { id: "dap", key: "dap", Icon: Lock, pillars: [KeyRound, ChartColumn, Lock, BadgeCheck], acronym: true },
  { id: "reg", key: "reg", Icon: Globe, pillars: [Settings, ChartLine, Link2, Search] },
  { id: "slm", key: "slm", Icon: GraduationCap, pillars: [Link2, Users, Zap, KeyRound], acronym: true },
  { id: "viaipe", key: "viaipe", Icon: Network, pillars: [BookOpen, ChartLine, Link2, CircleCheck] },
  { id: "moodle-cr", key: "moodle", Icon: GraduationCap, pillars: [Cloud, Lock, BadgeCheck, Lightbulb] },
  { id: "bcd", key: "bcd", Icon: FileBadge, pillars: [Link2, Search, Network, ClipboardCheck], acronym: true },
  { id: "sso-calriz", key: "sso", Icon: KeyRound, pillars: [LogIn, KeyRound, Users, ShieldCheck] },
  { id: "clm", key: "clm", Icon: FileText, pillars: [Files, Bell, ChartColumn, ClipboardCheck], acronym: true },
  { id: "dedicated-it", key: "dedicated", Icon: Server, pillars: [Server, Layers, Lock, Activity] },
  { id: "captive-portal", key: "captive", Icon: Wifi, pillars: [Palette, KeyRound, GraduationCap, ChartColumn] },
  { id: "isp", key: "isp", Icon: RadioTower, pillars: [School, ShieldCheck, Network, Activity], acronym: true },
  { id: "net", key: "net", Icon: Activity, pillars: [Activity, BellRing, Gauge, ChartLine], acronym: true },
];

const CORE_ICONS = [Users, Settings, ChartColumn, Zap];
const CORE_ID = "calriz-core";

// Menu "Produtos" do header: os produtos por tema (ids da página; o Core fica sozinho em "Plataforma").
const MENU_GROUPS: { key: string; Icon: LucideIcon; ids: string[] }[] = [
  { key: "education", Icon: GraduationCap, ids: ["slm", "moodle-cr", "bcd"] },
  { key: "government", Icon: Landmark, ids: ["dap", "reg"] },
  { key: "identity", Icon: KeyRound, ids: ["sso-calriz", "clm", "dedicated-it"] },
  { key: "connectivity", Icon: Wifi, ids: ["viaipe", "captive-portal", "isp", "net"] },
  { key: "platform", Icon: Layers, ids: [CORE_ID] },
];

const list = <T,>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

export function useProdutos() {
  const { t } = useTranslation();

  const products: Product[] = PRODUCTS_CONFIG.map(({ id, key, Icon, pillars }) => {
    const k = `produtos_page.products.${key}`;
    const titles = list<string>(t(`${k}.pillars`, { returnObjects: true }));
    return {
      id,
      Icon,
      name: t(`${k}.name`),
      accent: t(`${k}.accent`),
      heading: t(`${k}.heading`),
      points: list<string>(t(`${k}.points`, { returnObjects: true })),
      pillars: titles.map((title, i) => ({ title, Icon: pillars[i % pillars.length] })),
      tip: t(`${k}.tip`),
    };
  });

  const features = list<{ title: string; text: string }>(t("produtos_page.core.features", { returnObjects: true }));
  const core: Core = {
    id: CORE_ID,
    Icon: Layers,
    name: t("produtos_page.core.name"),
    accent: t("produtos_page.core.accent"),
    text: t("produtos_page.core.text"),
    flowLabel: t("produtos_page.core.flow_label"),
    flow: list<string>(t("produtos_page.core.flow", { returnObjects: true })),
    features: features.map((feature, i) => ({ ...feature, Icon: CORE_ICONS[i % CORE_ICONS.length] })),
    tip: t("produtos_page.core.tip"),
  };

  return { products, core };
}

// Cada grupo leva ao primeiro produto dele na página; cada item, à seção do produto.
export function useProdutoMenuGroups(): MenuGroup[] {
  const { t } = useTranslation();
  const name = (id: string) => {
    const product = PRODUCTS_CONFIG.find((p) => p.id === id);
    if (!product) return t("produtos_page.core.name");
    const k = `produtos_page.products.${product.key}`;
    return product.acronym ? `${t(`${k}.name`)} · ${t(`${k}.accent`)}` : t(`${k}.name`);
  };

  return MENU_GROUPS.map(({ key, Icon, ids }) => ({
    key,
    title: t(`header.products_menu.groups.${key}`),
    icon: Icon,
    to: `/produtos#${ids[0]}`,
    items: ids.map((id) => ({ key: id, label: name(id), to: `/produtos#${id}` })),
  }));
}
